import { forwardRef, type SVGProps } from "react";

type BibleIconProps = SVGProps<SVGSVGElement> & {
  color?: string;
};

export const BibleIcon = forwardRef<SVGSVGElement, BibleIconProps>(
  ({ color = "white", ...props }, ref) => {
    return (
      <svg
        aria-hidden="true"
        ref={ref}
        {...props}
        xmlns="http://www.w3.org/2000/svg"
        width="20"
        height="20"
        viewBox="0 0 20 20"
        fill="none"
      >
        <path
          d="M3.05566 15.0556V4.22222C3.05566 2.99444 4.05011 2 5.27789 2H16.9446V13.1111"
          stroke={color}
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M6 4.85156V10.3516"
          stroke={color}
          strokeWidth="1.5"
          strokeLinecap="round"
        />
        <path
          d="M11.2223 15.0547H6.77783V18.9436C6.77783 19.168 6.91339 19.3714 7.12117 19.4569C7.32894 19.5447 7.56672 19.4958 7.72672 19.3369L9.00005 18.0636L10.2734 19.3369C10.3801 19.4436 10.5223 19.4991 10.6667 19.4991C10.7378 19.4991 10.8112 19.4858 10.8789 19.4569C11.0867 19.3714 11.2223 19.168 11.2223 18.9436V15.0547Z"
          fill={color}
        />
        <path
          d="M5.00011 16.9983C3.92678 16.9983 3.05566 16.1283 3.05566 15.0538C3.05566 13.9794 3.92678 13.1094 5.00011 13.1094H16.9446C16.2323 14.0472 16.129 15.9394 16.9446 16.9983L13 16.9983"
          stroke={color}
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    );
  },
);
BibleIcon.displayName = "BibleIcon";

type ChurchIconProps = SVGProps<SVGSVGElement> & {
  color?: string;
};

export const ChurchIcon = forwardRef<SVGSVGElement, ChurchIconProps>(
  ({ color = "white", ...props }, ref) => {
    return (
      <svg
        aria-hidden="true"
        ref={ref}
        {...props}
        xmlns="http://www.w3.org/2000/svg"
        width="20"
        height="20"
        viewBox="0 0 20 20"
        fill="none"
      >
        <path
          d="M10 1.5V7M10 7L4.25 11.5179M10 7L15.75 11.5178M12 3.56785H8M15.75 11.5178V18.5H4.25V11.5179M15.75 11.5178L17 12.5M4.25 11.5179L3 12.5M8.33325 18.5V15.1667C8.33325 14.2462 9.07942 13.5 9.99992 13.5C10.9204 13.5 11.6666 14.2462 11.6666 15.1667V18.5"
          stroke={color}
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    );
  },
);
ChurchIcon.displayName = "ChurchIcon";

interface ExploreMoreIconProps extends SVGProps<SVGSVGElement> {
  color?: string;
}

export const ExploreMoreIcon = forwardRef<SVGSVGElement, ExploreMoreIconProps>(
  ({ color = "currentColor", ...props }, ref) => {
    return (
      <svg
        ref={ref}
        {...props}
        xmlns="http://www.w3.org/2000/svg"
        width="16"
        height="16"
        viewBox="0 0 16 16"
        fill="none"
        aria-hidden="true"
      >
        <path
          d="M7.99992 14.6693C11.6818 14.6693 14.6666 11.6845 14.6666 8.0026C14.6666 4.32071 11.6818 1.33594 7.99992 1.33594C4.31802 1.33594 1.33325 4.32071 1.33325 8.0026C1.33325 11.6845 4.31802 14.6693 7.99992 14.6693Z"
          stroke={color}
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M10.689 4.69521C11.0597 4.59317 11.4098 4.94319 11.3077 5.31392L11.278 5.39334L9.67113 8.92956C9.52108 9.25929 9.25647 9.52393 8.92673 9.67396L5.39051 11.2808C4.9658 11.4738 4.52928 11.0371 4.72207 10.6124L6.32894 7.07617C6.47899 6.74633 6.7435 6.48182 7.07334 6.33177L10.6096 4.7249L10.689 4.69521ZM8.70714 7.29576C8.31662 6.90523 7.68345 6.90523 7.29293 7.29576C6.90245 7.68628 6.90242 8.31946 7.29293 8.70997C7.68346 9.1002 8.31671 9.10036 8.70714 8.70997C9.09757 8.31954 9.09738 7.6863 8.70714 7.29576Z"
          fill={color}
        />
      </svg>
    );
  },
);

ExploreMoreIcon.displayName = "ExploreMoreIcon";

interface HeadphonesIconProps extends SVGProps<SVGSVGElement> {
  color?: string;
  title?: string;
}

export const HeadphonesIcon = forwardRef<SVGSVGElement, HeadphonesIconProps>(
  (
    {
      color = "white",
      title = "Headphones",
      "aria-hidden": ariaHidden = true,
      ...props
    },
    ref,
  ) => {
    return (
      <svg
        ref={ref}
        xmlns="http://www.w3.org/2000/svg"
        width="20"
        height="20"
        viewBox="0 0 20 20"
        fill="none"
        aria-hidden={ariaHidden}
        role={props.role}
        {...props}
      >
        <title>{title}</title>
        <path
          d="M16.7374 14.1667C17.1541 12.9167 17.5 11.1973 17.5 10C17.5 5.85787 14.1422 2.5 10 2.5C5.85787 2.5 2.5 5.85787 2.5 10C2.5 11.1973 2.84593 12.9167 3.2626 14.1667M7.48042 16.3369L6.03214 12.1772C5.87784 11.7888 5.38475 11.5942 4.97911 11.6914C3.72034 12.1111 3.0385 13.4765 3.45618 14.7413L3.82119 15.8465C4.23887 17.1112 5.59789 17.7962 6.85666 17.3766C7.2408 17.213 7.58707 16.7414 7.48042 16.3369ZM12.5196 16.3369L13.9678 12.1772C14.1222 11.7888 14.6153 11.5942 15.0209 11.6914C16.2797 12.1111 16.9615 13.4765 16.5438 14.7413L16.1788 15.8465C15.7612 17.1112 14.4021 17.7962 13.1433 17.3766C12.7592 17.213 12.4129 16.7414 12.5196 16.3369Z"
          stroke={color}
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    );
  },
);
HeadphonesIcon.displayName = "HeadphonesIcon";

interface OpenBookIconProps extends SVGProps<SVGSVGElement> {
  color?: string;
}

export const OpenBookIcon = forwardRef<SVGSVGElement, OpenBookIconProps>(
  ({ color = "white", ...props }, ref) => {
    return (
      <svg
        ref={ref}
        {...props}
        xmlns="http://www.w3.org/2000/svg"
        width="16"
        height="16"
        viewBox="0 0 16 16"
        fill="none"
        aria-hidden="true"
      >
        <path
          d="M13.6934 3.98828C13.6933 3.93176 13.6668 3.89144 13.6309 3.87012C13.1394 3.58001 12.3029 3.20705 11.2207 3.20703C10.0895 3.20703 9.22842 3.61607 8.75 3.9082V12.2695C9.38575 11.9978 10.2276 11.7574 11.2324 11.7588C12.1412 11.7598 12.9171 11.9576 13.5244 12.1973H13.5234C13.5587 12.2111 13.5946 12.2087 13.6318 12.1826C13.6707 12.1553 13.6933 12.1131 13.6934 12.0625V3.98828ZM15.1934 12.0625C15.1933 13.1856 14.0658 14.0222 12.9746 13.5928H12.9736C12.5671 13.4323 12.0652 13.2974 11.4844 13.2656L11.2305 13.2588C10.15 13.2573 9.3121 13.6221 8.81445 13.9092L8.8125 13.9102C8.56391 14.0528 8.28525 14.1289 8 14.1289C7.71509 14.1289 7.4364 14.0531 7.1875 13.9102L7.18652 13.9092C6.68897 13.6221 5.85098 13.2573 4.77051 13.2588H4.76953C4.08042 13.2596 3.49093 13.4094 3.02637 13.5928V13.5938C1.939 14.0217 0.806892 13.1927 0.806641 12.0664V3.98633C0.806641 3.41045 1.10418 2.8761 1.60742 2.5791L1.86914 2.43359C2.52854 2.08583 3.5269 1.7081 4.7793 1.70801C6.24482 1.70801 7.36208 2.22661 7.99805 2.60938C8.63339 2.2264 9.75242 1.70703 11.2207 1.70703C12.6519 1.70705 13.7516 2.19983 14.3926 2.57812C14.8986 2.87675 15.1933 3.41588 15.1934 3.98828V12.0625ZM2.30664 12.0664C2.30675 12.1156 2.32918 12.1559 2.36719 12.1826C2.40361 12.2081 2.43932 12.2113 2.47559 12.1973C3.08278 11.9576 3.85893 11.7599 4.76758 11.7588H4.76855C5.77294 11.7574 6.6144 11.9979 7.25 12.2695V3.91016C6.77161 3.61802 5.91062 3.20801 4.7793 3.20801C3.69735 3.20811 2.86156 3.58103 2.37012 3.87109C2.33114 3.8941 2.30664 3.93288 2.30664 3.98633V12.0664Z"
          fill={color}
        />
      </svg>
    );
  },
);

OpenBookIcon.displayName = "OpenBookIcon";

type TrendingItemsIconProps = SVGProps<SVGSVGElement> & {
  color?: string;
};

export function TrendingItemsIcon({
  className,
  width = 16,
  height = 16,
  color = "#808080",
  ...props
}: TrendingItemsIconProps) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={width}
      height={height}
      viewBox="0 0 16 16"
      fill="none"
      className={className}
      aria-hidden="true"
      focusable="false"
      {...props}
    >
      <path
        d="M8.66667 4.66406H14M14 4.66406V9.9974M14 4.66406L8.66667 9.9974L6 7.33073L2 11.3307"
        stroke={color}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

type UpArrowProps = SVGProps<SVGSVGElement> & {
  color?: string;
};

export function UpArrow({ color = "#101010", ...props }: UpArrowProps) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      aria-hidden="true"
      {...props}
    >
      <path
        d="M13.65 6.7749L8.39999 1.5999C8.17499 1.3749 7.82499 1.3749 7.59999 1.5999L2.34999 6.7749C2.12499 6.9999 2.12499 7.3499 2.34999 7.5749C2.57499 7.7999 2.92499 7.7999 3.14999 7.5749L7.42499 3.3749V13.9999C7.42499 14.2999 7.67499 14.5499 7.97499 14.5499C8.27499 14.5499 8.54999 14.2999 8.54999 13.9999V3.3249L12.875 7.5749C12.975 7.6749 13.125 7.7249 13.275 7.7249C13.425 7.7249 13.575 7.6749 13.675 7.5499C13.875 7.3499 13.875 6.9749 13.65 6.7749Z"
        fill={color}
      />
    </svg>
  );
}
